import { useState, useEffect } from 'react';
import { View, Text, ScrollView, Alert, Modal, Platform, ActivityIndicator, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../shared/utils/api';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { Card, Button, TabButton, Input, Badge, PasswordInput } from '../../components/ui';
import { AccountRequest, User } from '../../shared/types/auth';
import { addColorOpacity } from '../../shared/utils/styles';
import { PageContainer, PageHeader } from '../../components/layout';
import { logger } from '../../shared/utils/logger';

type AdminTab = 'dashboard' | 'requests' | 'users' | 'analytics' | 'realtime';

interface AnalyticsOverview {
  activeUsers: { dau: string; wau: string; mau: string };
  sessions: { total_sessions: string; today_sessions: string; avg_duration_ms: number; avg_page_views: number };
  events: { total_events: string; today_events: string; unique_users: string };
  topPages: Array<{ page: string; views: string; unique_visitors: string }>;
  featureUsage: Array<{ event_type: string; usage_count: string; unique_users: string }>;
  deviceBreakdown: Array<{ device_type: string; count: string; percentage: string }>;
  platformBreakdown: Array<{ platform: string; count: string; percentage: string }>;
  period: string;
}

interface RealtimeAnalytics {
  activeUsers: number;
  events: number;
  recentEvents: Array<{ eventType: string; page: string; timestamp: string; user: string }>;
  activePages: Array<{ page: string; views: string }>;
  asOf: string;
}

interface AdminStatistics {
  users: {
    total_users: string;
    admin_count: string;
    user_count: string;
    verified_users: string;
    new_users_30d: string;
    active_users_30d: string;
    never_logged_in: string;
  };
  accountRequests: {
    total_requests: string;
    pending_requests: string;
    approved_requests: string;
    denied_requests: string;
    new_requests_30d: string;
  };
  portfolios: {
    total_portfolios: string;
    new_portfolios_30d: string;
  };
  properties: {
    total_properties: string;
    research_properties: string;
    under_contract_properties: string;
    owned_properties: string;
    listed_properties: string;
    sold_properties: string;
    new_properties_30d: string;
    total_property_value: string;
  };
  financial: {
    total_entries: string;
    income_entries: string;
    expense_entries: string;
    total_income: string;
    total_expenses: string;
    new_entries_30d: string;
  };
  userGrowth: Array<{ month: string; count: number }>;
}

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [statistics, setStatistics] = useState<AdminStatistics | null>(null);
  const [analyticsOverview, setAnalyticsOverview] = useState<AnalyticsOverview | null>(null);
  const [realtimeData, setRealtimeData] = useState<RealtimeAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'denied'>('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'approve' | 'deny' | null>(null);
  const [modalRequestId, setModalRequestId] = useState<number | null>(null);
  const [modalNotes, setModalNotes] = useState('');
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState<Set<number>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [userVerifiedFilter, setUserVerifiedFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  const [userDetailsModal, setUserDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetPasswordNew, setResetPasswordNew] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  
  // Responsive values for typography
  const headingSize = getResponsiveValue(28, 30, 32, 32);
  const bodySize = getResponsiveValue(15, 15, 16, 16);

  // Check if user is admin
  useEffect(() => {
    if (!user) {
      return; // Wait for user to load
    }
    
    if (user.role !== 'admin') {
      Alert.alert('Access Denied', 'Admin access required');
      router.replace('/(tabs)/home');
      return;
    }
  }, [user, router]);

  // Load data when tab changes or when user becomes available
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      return;
    }

    logger.debug('Loading data for tab', 'admin', { tab: activeTab });
    
    // Load data for the active tab
    if (activeTab === 'dashboard') {
      logger.debug('Loading statistics', 'admin');
      loadStatistics();
    } else if (activeTab === 'requests') {
      logger.debug('Loading requests', 'admin');
      loadRequests();
    } else if (activeTab === 'users') {
      logger.debug('Loading users', 'admin');
      loadUsers();
    } else if (activeTab === 'analytics') {
      logger.debug('Loading analytics', 'admin');
      loadAnalytics();
    } else if (activeTab === 'realtime') {
      logger.debug('Loading realtime', 'admin');
      loadRealtime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  // Reload requests when filter changes
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      return;
    }
    if (activeTab === 'requests') {
      loadRequests();
    }
  }, [filter]);

  const loadStatistics = async () => {
    try {
      setStatsLoading(true);
      logger.debug('Fetching statistics from API', 'admin');
      const response = await authApi.getAdminStatistics();
      // Never log full statistics response - contains sensitive data
      logger.debug('Statistics received successfully', 'admin');
      setStatistics(response.statistics);
    } catch (error: any) {
      // Only log in development - don't expose error details in production
      logger.error('Load statistics error', error, 'admin');
      Alert.alert('Error', error.message || 'Failed to load statistics');
      setStatistics(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      logger.debug('Fetching analytics from API', 'admin');
      const response = await authApi.getAnalyticsOverview(analyticsDays);
      logger.debug('Analytics received successfully', 'admin');
      setAnalyticsOverview(response.analytics);
    } catch (error: any) {
      logger.error('Load analytics error', error, 'admin');
      // Don't show alert - analytics might not be set up yet
      setAnalyticsOverview(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadRealtime = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await authApi.getAnalyticsRealtime();
      setRealtimeData(response.realtime);
    } catch (error: any) {
      logger.error('Load realtime error', error, 'admin');
      setRealtimeData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? undefined : filter;
      logger.debug('Fetching account requests', 'admin', { filter: status });
      const response = await authApi.getAccountRequests(status);
      logger.debug('Requests received', 'admin', { count: response.requests?.length || 0 });
      setRequests(response.requests || []);
      setSelectedRequests(new Set());
    } catch (error: any) {
      // Only log in development - don't expose error details in production
      logger.error('Load requests error', error, 'admin');
      Alert.alert('Error', error.message || 'Failed to load account requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const params: any = {};
      // Sanitize search input - trim and limit length
      if (userSearch) {
        const sanitizedSearch = userSearch.trim().slice(0, 255);
        if (sanitizedSearch) {
          params.search = sanitizedSearch;
        }
      }
      if (userRoleFilter !== 'all') params.role = userRoleFilter;
      if (userVerifiedFilter === 'verified') params.email_verified = true;
      else if (userVerifiedFilter === 'unverified') params.email_verified = false;
      params.limit = 100;
      
      // Never log search params - could contain user emails
      logger.debug('Fetching users', 'admin');
      const response = await authApi.searchUsers(params);
      logger.debug('Users received', 'admin', { count: response.users?.length || 0 });
      setUsers(response.users || []);
    } catch (error: any) {
      // Only log in development - don't expose error details in production
      logger.error('Load users error', error, 'admin');
      Alert.alert('Error', error.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // Reload users when filters change (but not on search - that's manual)
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      return;
    }
    if (activeTab === 'users' && !userSearch) {
      // Only auto-reload when filters change, not when typing in search
      loadUsers();
    }
  }, [userRoleFilter, userVerifiedFilter]);

  const openApproveModal = (id: number) => {
    setModalType('approve');
    setModalRequestId(id);
    setModalNotes('');
    setModalVisible(true);
  };

  const openDenyModal = (id: number) => {
    setModalType('deny');
    setModalRequestId(id);
    setModalNotes('');
    setModalVisible(true);
  };

  const handleModalConfirm = async () => {
    if (!modalRequestId || !modalType) return;

    try {
      // Sanitize notes - trim and limit length
      const sanitizedNotes = modalNotes.trim().slice(0, 1000) || undefined;
      
      if (modalType === 'approve') {
        await authApi.approveAccountRequest(modalRequestId, sanitizedNotes);
        Alert.alert('Success', 'Account request approved');
      } else {
        await authApi.denyAccountRequest(modalRequestId, sanitizedNotes);
        Alert.alert('Success', 'Account request denied');
      }
      setModalVisible(false);
      setModalType(null);
      setModalRequestId(null);
      setModalNotes('');
      loadRequests();
      if (activeTab === 'dashboard') loadStatistics();
    } catch (error: any) {
      Alert.alert('Error', error.message || `Failed to ${modalType} request`);
    }
  };

  const handleBulkAction = async (action: 'approve' | 'deny') => {
    if (selectedRequests.size === 0) {
      Alert.alert('No Selection', 'Please select at least one request');
      return;
    }

    try {
      // Sanitize notes - trim and limit length
      const sanitizedNotes = modalNotes.trim().slice(0, 1000) || undefined;
      
      const response = await authApi.bulkAccountRequestAction(
        Array.from(selectedRequests),
        action,
        sanitizedNotes
      );
      Alert.alert('Success', response.message);
      setBulkModalVisible(false);
      setModalNotes('');
      setSelectedRequests(new Set());
      loadRequests();
      if (activeTab === 'dashboard') loadStatistics();
    } catch (error: any) {
      Alert.alert('Error', error.message || `Failed to ${action} requests`);
    }
  };

  const toggleRequestSelection = (id: number) => {
    const newSelection = new Set(selectedRequests);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedRequests(newSelection);
  };

  const selectAllRequests = () => {
    const pendingRequests = requests.filter(r => r.status === 'pending');
    setSelectedRequests(new Set(pendingRequests.map(r => r.id)));
  };

  const deselectAllRequests = () => {
    setSelectedRequests(new Set());
  };

  const handleUpdateUserRole = async (userId: number, newRole: 'admin' | 'user') => {
    try {
      await authApi.updateUserRole(userId, newRole);
      Alert.alert('Success', `User role updated to ${newRole}`);
      loadUsers();
      if (activeTab === 'dashboard') loadStatistics();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to update user role');
    }
  };

  const loadUserDetails = async (userItem: User) => {
    try {
      setUserDetailsLoading(true);
      setSelectedUser(userItem);
      const response = await authApi.getUserDetails(userItem.id);
      setUserDetails(response);
      setUserDetailsModal(true);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to load user details');
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      Alert.alert('Error', 'New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      Alert.alert('Error', 'Password must be at least 8 characters long');
      return;
    }

    setChangePasswordLoading(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      Alert.alert('Success', 'Password changed successfully');
      setChangePasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to change password');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleResetUserPassword = async () => {
    if (!resetPasswordUserId || !resetPasswordNew || !resetPasswordConfirm) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }

    if (resetPasswordNew !== resetPasswordConfirm) {
      Alert.alert('Error', 'Passwords do not match');
      return;
    }

    if (resetPasswordNew.length < 8) {
      Alert.alert('Error', 'Password must be at least 8 characters long');
      return;
    }

    setResetPasswordLoading(true);
    try {
      await authApi.resetUserPassword(resetPasswordUserId, resetPasswordNew);
      Alert.alert('Success', `Password reset successfully for ${resetPasswordEmail}`);
      setResetPasswordModal(false);
      setResetPasswordUserId(null);
      setResetPasswordEmail('');
      setResetPasswordNew('');
      setResetPasswordConfirm('');
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatNumber = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'approved':
        return tokens.color.success;
      case 'denied':
        return tokens.color.error;
      default:
        return tokens.color.onSurface;
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <PageContainer>
        <Text style={[
          {
            fontSize: headingSize,
            fontWeight: utils.typography.weights.bold,
            marginBottom: utils.spacing.xs,
            letterSpacing: -0.5,
            color: tokens.color.onBackground,
            fontFamily: utils.typography.heading.fontFamily,
            lineHeight: headingSize * 1.2,
          }
        ]}>Access Denied</Text>
        <Text style={[
          {
            fontSize: bodySize,
            color: tokens.color.onBackground,
            fontFamily: utils.typography.body.fontFamily,
            lineHeight: bodySize * 1.5,
          }
        ]}>Admin access required</Text>
      </PageContainer>
    );
  }

  return (
    <PageContainer verticalPadding="2xl">
      <PageHeader
        title="Enterprise Admin Panel"
        subtitle="Manage users, requests, and system analytics"
      />

      {/* Change Password Button */}
      <View style={{ marginBottom: utils.spacing.md }}>
        <Button
          title="Change My Password"
          onPress={() => {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setChangePasswordModal(true);
          }}
          variant="outline"
        />
      </View>

      {/* Tab switcher */}
      <View style={{ flexDirection: 'row', marginBottom: utils.spacing.xl, gap: utils.spacing.sm, flexWrap: 'wrap' }}>
        {(['dashboard', 'requests', 'users', 'analytics', 'realtime'] as AdminTab[]).map((tab) => (
          <TabButton
            key={tab}
            title={tab === 'realtime' ? 'Live' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            onPress={() => setActiveTab(tab)}
            active={activeTab === tab}
          />
        ))}
      </View>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          {statsLoading ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="large" color={tokens.color.primary} />
            <Text style={styles.loadingText}>Loading statistics...</Text>
            </View>
          ) : statistics ? (
            <>
              {/* Key Metrics Cards */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.md, marginBottom: utils.spacing.lg }}>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Total Users</Text>
                  <Text style={[styles.heading('2xl'), { marginBottom: utils.spacing.xs }]}>{formatNumber(statistics.users.total_users)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                    {formatNumber(statistics.users.new_users_30d)} new (30d)
                  </Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Pending Requests</Text>
                  <Text style={[styles.heading('2xl'), { color: tokens.color.secondary, marginBottom: utils.spacing.xs }]}>
                    {formatNumber(statistics.accountRequests.pending_requests)}
                  </Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                    {formatNumber(statistics.accountRequests.total_requests)} total
                  </Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Portfolios</Text>
                  <Text style={[styles.heading('2xl'), { marginBottom: utils.spacing.xs }]}>{formatNumber(statistics.portfolios.total_portfolios)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                    {formatNumber(statistics.portfolios.new_portfolios_30d)} new (30d)
                  </Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Properties</Text>
                  <Text style={[styles.heading('2xl'), { marginBottom: utils.spacing.xs }]}>{formatNumber(statistics.properties.total_properties)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                    {formatCurrency(statistics.properties.total_property_value)} value
                  </Text>
                </Card>
              </View>

              {/* Detailed Statistics */}
              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>User Statistics</Text>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Admins:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.users.admin_count)}</Text>
                </View>
                </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Regular Users:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.users.user_count)}</Text>
                </View>
                </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Verified:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.users.verified_users)}</Text>
                </View>
              </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Active (30d):</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.users.active_users_30d)}</Text>
                  </View>
                </View>
                <View style={{ paddingVertical: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={styles.text('base')}>Never Logged In:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.users.never_logged_in)}</Text>
                  </View>
                </View>
              </Card>

              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>Property Statistics</Text>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Owned:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.properties.owned_properties)}</Text>
                </View>
                </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Research:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.properties.research_properties)}</Text>
                </View>
                </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Under Contract:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.properties.under_contract_properties)}</Text>
                </View>
              </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Listed:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.properties.listed_properties)}</Text>
                  </View>
                </View>
                <View style={{ paddingVertical: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={styles.text('base')}>Sold:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold }]}>{formatNumber(statistics.properties.sold_properties)}</Text>
                  </View>
                </View>
              </Card>

              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>Financial Statistics</Text>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Total Income:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold, color: tokens.color.success }]}>
                    {formatCurrency(statistics.financial.total_income)}
                  </Text>
                </View>
                </View>
                <View style={{ borderBottomWidth: 1, borderBottomColor: addColorOpacity(tokens.color.border, 0.4), marginBottom: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.sm }}>
                    <Text style={styles.text('base')}>Total Expenses:</Text>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold, color: tokens.color.error }]}>
                    {formatCurrency(statistics.financial.total_expenses)}
                  </Text>
                </View>
                </View>
                <View style={{ paddingVertical: utils.spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={styles.text('base')}>Net:</Text>
                  <Text style={[
                      styles.text('base'),
                      { fontWeight: utils.typography.weights.semibold },
                    {
                      color: parseFloat(statistics.financial.total_income) - parseFloat(statistics.financial.total_expenses) >= 0
                        ? tokens.color.success
                        : tokens.color.error
                    }
                  ]}>
                    {formatCurrency(
                      parseFloat(statistics.financial.total_income) - parseFloat(statistics.financial.total_expenses)
                    )}
                  </Text>
                </View>
              </View>
              </Card>
            </>
          ) : (
            <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No statistics available</Text>
            </View>
          )}
        </>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <>
          {/* Filter buttons */}
          <View style={{ flexDirection: 'row', marginBottom: utils.spacing.lg, gap: utils.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            {(['all', 'pending', 'approved', 'denied'] as const).map((f) => (
              <TabButton
                key={f}
                title={f.charAt(0).toUpperCase() + f.slice(1)}
                onPress={() => setFilter(f)}
                active={filter === f}
              />
            ))}
          </View>

          {/* Bulk Actions */}
          {filter === 'pending' && requests.length > 0 && (
            <Card style={{ marginBottom: utils.spacing.md }}>
              <Text style={[styles.text('base'), { marginBottom: utils.spacing.sm }]}>
                {selectedRequests.size} of {requests.length} selected
              </Text>
              <View style={{ flexDirection: 'row', gap: utils.spacing.sm, flexWrap: 'wrap' }}>
                <Button
                  title={selectedRequests.size === requests.length ? 'Deselect All' : 'Select All'}
                  onPress={selectedRequests.size === requests.length ? deselectAllRequests : selectAllRequests}
                  variant="outline"
                />
                {selectedRequests.size > 0 && (
                  <>
                    <Button
                      title="Bulk Approve"
                      onPress={() => {
                        setModalType('approve');
                        setBulkModalVisible(true);
                      }}
                      variant="primary"
                    />
                    <Button
                      title="Bulk Deny"
                      onPress={() => {
                        setModalType('deny');
                        setBulkModalVisible(true);
                      }}
                      variant="danger"
                    />
                  </>
                )}
              </View>
            </Card>
          )}

          {loading ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="large" color={tokens.color.primary} />
            <Text style={styles.loadingText}>Loading...</Text>
            </View>
          ) : requests.length === 0 ? (
            <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No account requests found</Text>
            </View>
          ) : (
            requests.map((request) => (
              <Card key={request.id} style={{ marginBottom: utils.spacing.md, flexDirection: 'row', gap: utils.spacing.md }}>
                {filter === 'pending' && (
                  <Pressable
                    onPress={() => toggleRequestSelection(request.id)}
                    style={{ width: 30, height: 30, justifyContent: 'center', alignItems: 'center' }}
                  >
                    <Text style={{ fontSize: utils.typography.sizes.xl, color: tokens.color.onSurface }}>
                      {selectedRequests.has(request.id) ? '☑' : '☐'}
                    </Text>
                  </Pressable>
                )}
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: utils.spacing.sm }}>
                    <Text style={[styles.text('lg'), { fontWeight: utils.typography.weights.semibold, flex: 1 }]}>{request.email}</Text>
                    <View style={{ paddingHorizontal: utils.spacing.sm, paddingVertical: utils.spacing.xs, borderRadius: utils.radius.sm, backgroundColor: addColorOpacity(getStatusColor(request.status), 0.2) }}>
                      <Text style={[styles.text('xs'), { fontWeight: utils.typography.weights.semibold, color: getStatusColor(request.status) }]}>
                        {request.status.toUpperCase()}
                      </Text>
                    </View>
                  </View>
                  
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginTop: utils.spacing.xs }]}>
                    Requested: {new Date(request.requested_at).toLocaleDateString()}
                  </Text>
                  
                  {request.reviewed_at && (
                    <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8) }]}>
                      Reviewed: {new Date(request.reviewed_at).toLocaleDateString()}
                      {request.reviewed_by_email && ` by ${request.reviewed_by_email}`}
                    </Text>
                  )}
                  
                  {request.notes && (
                    <Text style={[styles.text('sm'), { marginTop: utils.spacing.sm, fontStyle: 'italic' }]}>Notes: {request.notes}</Text>
                  )}

                  {request.status === 'pending' && (
                    <View style={{ flexDirection: 'row', gap: utils.spacing.md, marginTop: utils.spacing.md, flexWrap: 'wrap' }}>
                      <Button
                        title="Approve"
                        onPress={() => openApproveModal(request.id)}
                        variant="primary"
                      />
                      <Button
                        title="Deny"
                        onPress={() => openDenyModal(request.id)}
                        variant="danger"
                      />
                    </View>
                  )}
                </View>
              </Card>
            ))
          )}
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Search and Filters */}
          <Card style={{ marginBottom: utils.spacing.md, padding: utils.spacing.md, overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', marginBottom: utils.spacing.md, gap: utils.spacing.sm, alignItems: 'center', minWidth: 0 }}>
              <View style={{ flex: 1, minWidth: 0, flexShrink: 1 }}>
                <Input
                  placeholder="Search users by email..."
                  value={userSearch}
                  onChangeText={(text) => {
                    // Limit input length for security
                    if (text.length <= 255) {
                      setUserSearch(text);
                    }
                  }}
                  onSubmitEditing={() => loadUsers()}
                  maxLength={255}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
              <Button
                title="Search"
                onPress={() => loadUsers()}
                variant="primary"
                size="md"
                style={{ flexShrink: 0 }}
              />
            </View>

            <View style={{ flexDirection: 'row', marginBottom: utils.spacing.sm, gap: utils.spacing.xs, alignItems: 'center', flexWrap: 'wrap' }}>
              <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.7), marginRight: utils.spacing.xs, fontWeight: utils.typography.weights.medium }]}>Role:</Text>
              {(['all', 'admin', 'user'] as const).map((role) => (
                <TabButton
                  key={role}
                  title={role.charAt(0).toUpperCase() + role.slice(1)}
                  onPress={() => setUserRoleFilter(role)}
                  active={userRoleFilter === role}
                />
              ))}
            </View>

            <View style={{ flexDirection: 'row', gap: utils.spacing.xs, alignItems: 'center', flexWrap: 'wrap' }}>
              <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.7), marginRight: utils.spacing.xs, fontWeight: utils.typography.weights.medium }]}>Verified:</Text>
              {(['all', 'verified', 'unverified'] as const).map((verified) => (
                <TabButton
                  key={verified}
                  title={verified.charAt(0).toUpperCase() + verified.slice(1)}
                  onPress={() => setUserVerifiedFilter(verified)}
                  active={userVerifiedFilter === verified}
                />
              ))}
            </View>
          </Card>

          {usersLoading ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="large" color={tokens.color.primary} />
            <Text style={styles.loadingText}>Loading users...</Text>
            </View>
          ) : users.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>No users found</Text>
            </View>
          ) : (
            users.map((userItem) => (
              <Card key={userItem.id} style={{ marginBottom: utils.spacing.sm, padding: utils.spacing.md, overflow: 'hidden', minWidth: 0 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: utils.spacing.xs, minWidth: 0 }}>
                  <View style={{ flex: 1, marginRight: utils.spacing.sm, minWidth: 0, flexShrink: 1 }}>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold, marginBottom: utils.spacing.xs }]} numberOfLines={1} ellipsizeMode="tail">{userItem.email}</Text>
                    <View style={{ flexDirection: 'row', gap: utils.spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                        Created {new Date(userItem.created_at || '').toLocaleDateString()}
                      </Text>
                      {userItem.last_login && (
                        <>
                          <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.4) }]}>•</Text>
                          <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                            Last login {new Date(userItem.last_login).toLocaleDateString()}
                          </Text>
                        </>
                      )}
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', gap: utils.spacing.xs, alignItems: 'center', flexShrink: 0 }}>
                    <Badge
                      label={userItem.role === 'admin' ? 'Admin' : 'User'}
                      variant={userItem.role === 'admin' ? 'primary' : 'default'}
                      size="sm"
                    />
                    {userItem.email_verified && (
                      <Badge
                        label="Verified"
                        variant="success"
                        size="sm"
                      />
                    )}
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: utils.spacing.sm, marginTop: utils.spacing.sm, flexWrap: 'wrap', minWidth: 0 }}>
                  <Button
                    title="View Details"
                    onPress={() => loadUserDetails(userItem)}
                    variant="outline"
                    size="md"
                  />
                  {userItem.role !== 'admin' && (
                    <Button
                      title="Make Admin"
                      onPress={() => handleUpdateUserRole(userItem.id, 'admin')}
                      variant="primary"
                      size="md"
                    />
                  )}
                  {userItem.role === 'admin' && userItem.id !== user?.id && (
                    <Button
                      title="Remove Admin"
                      onPress={() => handleUpdateUserRole(userItem.id, 'user')}
                      variant="danger"
                      size="md"
                    />
                  )}
                </View>
              </Card>
            ))
          )}
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <>
          {/* Period selector */}
          <View style={{ flexDirection: 'row', marginBottom: utils.spacing.md, gap: utils.spacing.xs, flexWrap: 'wrap' }}>
            {[7, 14, 30, 90].map((days) => (
              <TabButton
                key={days}
                title={`${days}d`}
                onPress={() => {
                  setAnalyticsDays(days);
                  loadAnalytics();
                }}
                active={analyticsDays === days}
              />
            ))}
            <Button
              title="Refresh"
              onPress={loadAnalytics}
              variant="outline"
              size="sm"
            />
          </View>

          {analyticsLoading ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="large" color={tokens.color.primary} />
              <Text style={styles.loadingText}>Loading analytics...</Text>
            </View>
          ) : analyticsOverview ? (
            <>
              {/* Active Users Cards */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.md, marginBottom: utils.spacing.lg }}>
                <Card style={{ flex: 1, minWidth: 100 }}>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.7), marginBottom: utils.spacing.xs }]}>DAU</Text>
                  <Text style={styles.heading('2xl')}>{formatNumber(analyticsOverview.activeUsers.dau)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>Today</Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 100 }}>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.7), marginBottom: utils.spacing.xs }]}>WAU</Text>
                  <Text style={styles.heading('2xl')}>{formatNumber(analyticsOverview.activeUsers.wau)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>7 days</Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 100 }}>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.7), marginBottom: utils.spacing.xs }]}>MAU</Text>
                  <Text style={styles.heading('2xl')}>{formatNumber(analyticsOverview.activeUsers.mau)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>30 days</Text>
                </Card>
              </View>

              {/* Sessions & Events */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.md, marginBottom: utils.spacing.lg }}>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Sessions</Text>
                  <Text style={styles.heading('xl')}>{formatNumber(analyticsOverview.sessions.total_sessions)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                    {formatNumber(analyticsOverview.sessions.today_sessions)} today
                  </Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Avg Session</Text>
                  <Text style={styles.heading('xl')}>
                    {analyticsOverview.sessions.avg_duration_ms 
                      ? `${Math.round(analyticsOverview.sessions.avg_duration_ms / 60000)}m` 
                      : '0m'}
                  </Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                    {analyticsOverview.sessions.avg_page_views || 0} pages/session
                  </Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Total Events</Text>
                  <Text style={styles.heading('xl')}>{formatNumber(analyticsOverview.events.total_events)}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                    {formatNumber(analyticsOverview.events.today_events)} today
                  </Text>
                </Card>
              </View>

              {/* Top Pages */}
              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>Top Pages</Text>
                {analyticsOverview.topPages.length > 0 ? (
                  analyticsOverview.topPages.map((page, index) => (
                    <View key={index} style={{ 
                      borderBottomWidth: index < analyticsOverview.topPages.length - 1 ? 1 : 0, 
                      borderBottomColor: addColorOpacity(tokens.color.border, 0.4), 
                      paddingVertical: utils.spacing.sm,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Text style={[styles.text('sm'), { flex: 1 }]} numberOfLines={1}>{page.page || '/'}</Text>
                      <View style={{ flexDirection: 'row', gap: utils.spacing.md }}>
                        <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.7) }]}>
                          {formatNumber(page.views)} views
                        </Text>
                        <Text style={[styles.text('sm'), { color: tokens.color.primary }]}>
                          {formatNumber(page.unique_visitors)} users
                        </Text>
                      </View>
                    </View>
                  ))
                ) : (
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>No page data yet</Text>
                )}
              </Card>

              {/* Feature Usage */}
              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>Feature Usage</Text>
                {analyticsOverview.featureUsage.length > 0 ? (
                  analyticsOverview.featureUsage.map((feature, index) => (
                    <View key={index} style={{ 
                      borderBottomWidth: index < analyticsOverview.featureUsage.length - 1 ? 1 : 0, 
                      borderBottomColor: addColorOpacity(tokens.color.border, 0.4), 
                      paddingVertical: utils.spacing.sm,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Text style={[styles.text('sm'), { flex: 1 }]}>
                        {feature.event_type.replace(/_/g, ' ')}
                      </Text>
                      <View style={{ flexDirection: 'row', gap: utils.spacing.md }}>
                        <Text style={[styles.text('sm'), { fontWeight: utils.typography.weights.semibold }]}>
                          {formatNumber(feature.usage_count)}x
                        </Text>
                        <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                          {formatNumber(feature.unique_users)} users
                        </Text>
                      </View>
                    </View>
                  ))
                ) : (
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>No feature data yet</Text>
                )}
              </Card>

              {/* Device & Platform Breakdown */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.md, marginBottom: utils.spacing.md }}>
                <Card style={{ flex: 1, minWidth: 200 }}>
                  <Text style={styles.sectionTitle}>Devices</Text>
                  {analyticsOverview.deviceBreakdown.map((device, index) => (
                    <View key={index} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.xs }}>
                      <Text style={styles.text('sm')}>{device.device_type}</Text>
                      <Text style={[styles.text('sm'), { fontWeight: utils.typography.weights.semibold }]}>{device.percentage}%</Text>
                    </View>
                  ))}
                </Card>
                <Card style={{ flex: 1, minWidth: 200 }}>
                  <Text style={styles.sectionTitle}>Platforms</Text>
                  {analyticsOverview.platformBreakdown.map((platform, index) => (
                    <View key={index} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: utils.spacing.xs }}>
                      <Text style={styles.text('sm')}>{platform.platform}</Text>
                      <Text style={[styles.text('sm'), { fontWeight: utils.typography.weights.semibold }]}>{platform.percentage}%</Text>
                    </View>
                  ))}
                </Card>
              </View>
            </>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>No analytics data available</Text>
              <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.5), marginTop: utils.spacing.sm }]}>
                Analytics will appear after users start interacting with the app
              </Text>
            </View>
          )}
        </>
      )}

      {/* Realtime Tab */}
      {activeTab === 'realtime' && (
        <>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: utils.spacing.md }}>
            <Text style={styles.text('sm')}>Live activity (last 5 minutes)</Text>
            <Button
              title="Refresh"
              onPress={loadRealtime}
              variant="outline"
              size="sm"
            />
          </View>

          {analyticsLoading ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="large" color={tokens.color.primary} />
              <Text style={styles.loadingText}>Loading live data...</Text>
            </View>
          ) : realtimeData ? (
            <>
              {/* Live Stats */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.md, marginBottom: utils.spacing.lg }}>
                <Card style={{ flex: 1, minWidth: 150, backgroundColor: tokens.color.success + '10' }}>
                  <Text style={[styles.text('sm'), { color: tokens.color.success }]}>Active Now</Text>
                  <Text style={[styles.heading('3xl'), { color: tokens.color.success }]}>{realtimeData.activeUsers}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>users online</Text>
                </Card>
                <Card style={{ flex: 1, minWidth: 150 }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8) }]}>Events (5m)</Text>
                  <Text style={styles.heading('3xl')}>{realtimeData.events}</Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>interactions</Text>
                </Card>
              </View>

              {/* Active Pages */}
              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>Active Pages</Text>
                {realtimeData.activePages.length > 0 ? (
                  realtimeData.activePages.map((page, index) => (
                    <View key={index} style={{ 
                      flexDirection: 'row', 
                      justifyContent: 'space-between', 
                      paddingVertical: utils.spacing.sm,
                      borderBottomWidth: index < realtimeData.activePages.length - 1 ? 1 : 0,
                      borderBottomColor: addColorOpacity(tokens.color.border, 0.4),
                    }}>
                      <Text style={styles.text('sm')}>{page.page}</Text>
                      <Badge label={`${page.views} views`} variant="primary" size="sm" />
                    </View>
                  ))
                ) : (
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>No active pages</Text>
                )}
              </Card>

              {/* Recent Events */}
              <Card style={{ marginBottom: utils.spacing.md }}>
                <Text style={styles.sectionTitle}>Recent Activity</Text>
                {realtimeData.recentEvents.length > 0 ? (
                  realtimeData.recentEvents.slice(0, 10).map((event, index) => (
                    <View key={index} style={{ 
                      flexDirection: 'row', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      paddingVertical: utils.spacing.sm,
                      borderBottomWidth: index < Math.min(realtimeData.recentEvents.length, 10) - 1 ? 1 : 0,
                      borderBottomColor: addColorOpacity(tokens.color.border, 0.4),
                    }}>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.text('sm'), { fontWeight: utils.typography.weights.medium }]}>
                          {event.eventType.replace(/_/g, ' ')}
                        </Text>
                        <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]} numberOfLines={1}>
                          {event.page || 'Unknown page'} • {event.user}
                        </Text>
                      </View>
                      <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </Text>
                    </View>
                  ))
                ) : (
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>No recent activity</Text>
                )}
              </Card>

              <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.4), textAlign: 'center' }]}>
                Last updated: {new Date(realtimeData.asOf).toLocaleTimeString()}
              </Text>
            </>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>No live data available</Text>
            </View>
          )}
        </>
      )}

      {/* Modal for approve/deny with notes */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {modalType === 'approve' ? 'Approve Request' : 'Deny Request'}
            </Text>
            <Text style={[styles.text('base'), { marginBottom: utils.spacing.sm }]}>Optional notes:</Text>
            <Input
              placeholder="Enter notes (optional)"
              value={modalNotes}
              onChangeText={(text) => {
                // Limit input length for security
                if (text.length <= 1000) {
                  setModalNotes(text);
                }
              }}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              maxLength={1000}
              style={{ marginBottom: utils.spacing.md, minHeight: 100 }}
            />
            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => setModalVisible(false)}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title={modalType === 'approve' ? 'Approve' : 'Deny'}
                onPress={handleModalConfirm}
                variant={modalType === 'approve' ? 'primary' : 'danger'}
                style={{ flex: 1 }}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        visible={bulkModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setBulkModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              Bulk {modalType === 'approve' ? 'Approve' : 'Deny'} Requests
            </Text>
            <Text style={[styles.text('base'), { marginBottom: utils.spacing.sm }]}>
              {selectedRequests.size} request(s) will be {modalType === 'approve' ? 'approved' : 'denied'}
            </Text>
            <Text style={[styles.text('base'), { marginBottom: utils.spacing.sm }]}>Optional notes:</Text>
            <Input
              placeholder="Enter notes (optional)"
              value={modalNotes}
              onChangeText={(text) => {
                // Limit input length for security
                if (text.length <= 1000) {
                  setModalNotes(text);
                }
              }}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              maxLength={1000}
              style={{ marginBottom: utils.spacing.md, minHeight: 100 }}
            />
            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => {
                  setBulkModalVisible(false);
                  setModalNotes('');
                }}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title={modalType === 'approve' ? 'Approve All' : 'Deny All'}
                onPress={() => handleBulkAction(modalType!)}
                variant={modalType === 'approve' ? 'primary' : 'danger'}
                style={{ flex: 1 }}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* User Details Modal */}
      <Modal
        visible={userDetailsModal}
        transparent={true}
        animationType="none"
        onRequestClose={() => setUserDetailsModal(false)}
      >
        <View style={styles.modalOverlay}>
          <ScrollView style={styles.modalContent} contentContainerStyle={{ paddingBottom: utils.spacing['2xl'] }}>
            <Text style={styles.modalTitle}>User Details</Text>
            {userDetailsLoading ? (
              <View style={styles.emptyState}>
                <ActivityIndicator size="large" color={tokens.color.primary} />
              <Text style={styles.loadingText}>Loading...</Text>
              </View>
            ) : userDetails ? (
              <>
                <View style={{ marginBottom: utils.spacing.md }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Email:</Text>
                  <Text style={styles.text('base')}>{userDetails.user.email}</Text>
                </View>
                <View style={{ marginBottom: utils.spacing.md }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Role:</Text>
                  <Text style={styles.text('base')}>{userDetails.user.role}</Text>
                </View>
                <View style={{ marginBottom: utils.spacing.md }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Portfolios:</Text>
                  <Text style={styles.text('base')}>{userDetails.portfolios.length}</Text>
                  {userDetails.portfolios.length > 0 && (
                    <View style={{ marginTop: utils.spacing.sm }}>
                      {userDetails.portfolios.map((portfolio: any) => (
                        <Card key={portfolio.id} style={{ marginBottom: utils.spacing.sm }}>
                          <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold, marginBottom: utils.spacing.xs }]}>{portfolio.name}</Text>
                          <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8) }]}>
                            {portfolio.property_count} properties • Created {new Date(portfolio.created_at).toLocaleDateString()}
                          </Text>
                        </Card>
                      ))}
                    </View>
                  )}
                </View>
                <View style={{ marginBottom: utils.spacing.md }}>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.xs }]}>Property Statistics:</Text>
                  <Text style={styles.text('base')}>
                    {userDetails.propertyStats.total_properties} total properties
                  </Text>
                  <Text style={styles.text('base')}>
                    {userDetails.propertyStats.owned_properties} owned properties
                  </Text>
                </View>
                <Button
                  title="Reset Password"
                  onPress={() => {
                    setResetPasswordUserId(selectedUser?.id || null);
                    setResetPasswordEmail(selectedUser?.email || '');
                    setResetPasswordNew('');
                    setResetPasswordConfirm('');
                    setResetPasswordModal(true);
                  }}
                  variant="danger"
                  style={{ marginTop: utils.spacing.md }}
                />
              </>
            ) : null}
            <Button
              title="Close"
              onPress={() => setUserDetailsModal(false)}
              variant="outline"
              style={{ marginTop: utils.spacing.lg }}
            />
          </ScrollView>
        </View>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        visible={changePasswordModal}
        transparent={true}
        animationType="none"
        onRequestClose={() => setChangePasswordModal(false)}
      >
        <View style={styles.modalOverlay}>
          <ScrollView style={styles.modalContent} contentContainerStyle={{ paddingBottom: utils.spacing['2xl'] }}>
            <Text style={styles.modalTitle}>Change My Password</Text>
            <Text style={[styles.text('sm'), { marginBottom: utils.spacing.md, color: addColorOpacity(tokens.color.onSurface, 0.8) }]}>
              Password must be at least 8 characters and include uppercase, lowercase, and numbers.
            </Text>
            <PasswordInput
              label="Current Password"
              placeholder="Enter current password"
              value={currentPassword}
              onChangeText={setCurrentPassword}
              style={{ marginBottom: utils.spacing.md }}
            />
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              value={newPassword}
              onChangeText={setNewPassword}
              style={{ marginBottom: utils.spacing.md }}
            />
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm new password"
              value={confirmNewPassword}
              onChangeText={setConfirmNewPassword}
              style={{ marginBottom: utils.spacing.lg }}
            />
            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => {
                  setChangePasswordModal(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title={changePasswordLoading ? 'Changing...' : 'Change Password'}
                onPress={handleChangePassword}
                variant="primary"
                style={{ flex: 1 }}
                disabled={changePasswordLoading}
              />
            </View>
          </ScrollView>
        </View>
      </Modal>

      {/* Reset User Password Modal */}
      <Modal
        visible={resetPasswordModal}
        transparent={true}
        animationType="none"
        onRequestClose={() => setResetPasswordModal(false)}
      >
        <View style={styles.modalOverlay}>
          <ScrollView style={styles.modalContent} contentContainerStyle={{ paddingBottom: utils.spacing['2xl'] }}>
            <Text style={styles.modalTitle}>Reset User Password</Text>
            <Text style={[styles.text('base'), { marginBottom: utils.spacing.sm }]}>
              Resetting password for: {resetPasswordEmail}
            </Text>
            <Text style={[styles.text('sm'), { marginBottom: utils.spacing.md, color: addColorOpacity(tokens.color.onSurface, 0.8) }]}>
              Password must be at least 8 characters and include uppercase, lowercase, and numbers.
            </Text>
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              value={resetPasswordNew}
              onChangeText={setResetPasswordNew}
              style={{ marginBottom: utils.spacing.md }}
            />
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm new password"
              value={resetPasswordConfirm}
              onChangeText={setResetPasswordConfirm}
              style={{ marginBottom: utils.spacing.lg }}
            />
            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => {
                  setResetPasswordModal(false);
                  setResetPasswordUserId(null);
                  setResetPasswordEmail('');
                  setResetPasswordNew('');
                  setResetPasswordConfirm('');
                }}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title={resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                onPress={handleResetUserPassword}
                variant="danger"
                style={{ flex: 1 }}
                disabled={resetPasswordLoading}
              />
            </View>
          </ScrollView>
        </View>
      </Modal>
    </PageContainer>
  );
}
